import Colors from '@dynatrace/strato-design-tokens/colors';

import type { Orientation } from '../../core/types/orientation.js';
import type { TruncationMode } from '../../typography/text-ellipsis/TextEllipsis.js';
import type { MappedObject } from './types/helpers/mapped-object.js';
import type { LegendPosition } from './types/legend.js';
import type { ValueRepresentation } from './types/scales.js';
import type { ChartTooltipConfig } from './types/tooltip.js';

/**
 * @internal
 */
export const SCALE_FALLBACK_COLOR = Colors.Background.Container.Neutral.Accent;

/** @internal */
export const SIXTY_FPS = 1000 / 60; // in ms

/**
 * @internal
 */
export const DEFAULT_DATA_POINT_RADIUS = 3;

/**
 * Colors
 * @internal
 */
export const DEFAULT_FILL_OPACITY = 0.8;

/**
 * Highlighted
 * @internal
 */
export const DEFAULT_HIGHLIGHTED_OPACITY = 0.7;

/**
 * An inactive fill is applied to every shape that is not
 * being hovered from the legend.
 * @internal
 */
export const DEFAULT_INACTIVE_FILL_OPACITY = 0.1;

/**
 * @internal
 */
export const INACTIVE_FILL_OPACITY_PIE_CHART = DEFAULT_INACTIVE_FILL_OPACITY;

/**
 * @internal
 */
export const INACTIVE_FILL_OPACITY_PIE_CHART_LABEL = 0.2;

/**
 * Default opacity to be used for band chart.
 * It applies for both stroke and fill color.
 * @internal
 */
export const DEFAULT_OPACITY_BAND_CHART = 0.5;
/**
 * @internal
 */
/**
 Stroke only has another value to differentiate it from line charts
 * @internal
*/
export const STROKE_ONLY_OPACITY_BAND_CHART = 0.8;
/**
 * Width in px applied to the stroke style
 * @internal
 */
export const DEFAULT_STROKE_WIDTH_BAND_CHART = 1;
/**
 * @internal
 */
export const defaultRatio = 'auto';

/**
 * @public
 */
export const DEFAULT_TRUNCATION_MODE = 'middle';
/**
 * @internal
 */
export const DEFAULT_VALUE_REPRESENTATION: ValueRepresentation = 'absolute';

/**
 * Default value accessor field name used when extracting values from objects
 * @internal
 */
export const DEFAULT_VALUE_ACCESSOR = 'value';

/**
 * @internal
 */
export const DEFAULT_SERIES_CURVE_SHAPE = 'linear';

/**
 * @internal
 */
export const DEFAULT_INFINITE_ZOOM = false;

/**
 * Inner padding for categorical-based bars
 * @internal
 */
export const CATEGORICAL_BARS_INNER_PADDING = 0.25;

/**
 * Outer padding for categorical-based bars
 * @internal
 */
export const CATEGORICAL_BARS_OUTER_PADDING = 0.15;

/**
 * @public
 */
export const CSS_DOWNLOAD_FILENAME = 'data.csv';

/**
 * @internal
 */
export const LEGEND_POSITION = Object.freeze<MappedObject<LegendPosition>>({
  Auto: 'auto',
  Bottom: 'bottom',
  Right: 'right',
});

/**
 * @internal
 */
export const TRUNCATION_MODE = Object.freeze<MappedObject<TruncationMode>>({
  End: 'end',
  Middle: 'middle',
  Start: 'start',
});

/**
 * Grail limits
 * @internal
 */
export const GRAIL_MIN_DOMAIN = new Date('1677-09-21T00:12:43.145224192Z');
/**
 * @internal
 */
export const GRAIL_MAX_DOMAIN = new Date('2262-04-11T23:47:16.854775807Z');
/**
 * @internal
 */
export const SWIPE_RELATIVE_THRESHOLD = 0.15;
/**
 * @internal
 */
export const PINCH_RELATIVE_THRESHOLD = 0.15;
/**
 * @internal
 */
export const RIGHT_BUTTON_ID = 2;

export const MIDDLE_BUTTON_ID = 4;

export const ChartOverlaysElevation = {
  crosshair: 1,
  dataPoints: 2,
  brush: 3,
  toolbar: 4,
};

/**
 * @internal
 */
export const FONT_BASE = 10;

/**
 * @internal
 */
export const SUFFIX_RATIO = 0.66;

export const LOADING_STATE_OPACITY = 40;

export const ORIENTATION: MappedObject<Orientation> = {
  Horizontal: 'horizontal',
  Vertical: 'vertical',
};

/**
 * @internal
 */
export const DEFAULT_CUSTOM_TOOLTIP_CONFIG: ChartTooltipConfig = {
  hidden: false,
};

/**
 * Preset font sizes used by `'auto'` text-size resolution in charts that
 * support fixed/auto text sizing (TreeMap, Honeycomb, ...). Iterated in
 * descending order to pick the largest size that fits.
 * @internal
 */
export const AUTO_TEXT_SIZE_PRESETS = [18, 16, 14, 12];
